import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { sessionRefreshDecision } from './sessionPolicy';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
const TOKEN_EXPIRY_THRESHOLD = 300; // Refresh if less than 5 minutes (300 seconds) remaining

const ApiClient = axios.create({
    baseURL: BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    withCredentials: true,
    timeout: 30000, // 30 second timeout
});

let isRefreshing = false;
let failedQueue = [];
let refreshInterval = null;
let notificationCallback = null;
let sessionTimeoutCallback = null;
let timeoutWarningShown = false;

const processQueue = (error, token = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

const clearAuthData = () => {
    localStorage.removeItem('jwt');
    localStorage.removeItem('refreshToken');
    stopTokenRefreshInterval();
};

const isTokenExpired = (token) => {
    if (!token) return true;

    try {
        const decoded = jwtDecode(token);
        const currentTime = Date.now() / 1000;
        return decoded.exp - currentTime < TOKEN_EXPIRY_THRESHOLD;
    } catch (error) {
        return true;
    }
};

const refreshAuthToken = async (silent = false) => {
    if (isRefreshing) {
        return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
        });
    }

    isRefreshing = true;

    try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
            throw new Error('No refresh token available');
        }

        const response = await axios({
            method: 'post',
            url: '/api/refresh',
            baseURL: BASE_URL,
            data: { refreshToken },
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            }
        });

        const { token: newToken, refreshToken: newRefreshToken } = response.data;

        if (!newToken) {
            throw new Error('Invalid token response');
        }

        localStorage.setItem('jwt', newToken);
        if (newRefreshToken) {
            localStorage.setItem('refreshToken', newRefreshToken);
        }

        // Only notify about successful refresh if not silent
        if (!silent && notificationCallback) {
            notificationCallback('Token refreshed successfully', 'success');
        }

        processQueue(null, newToken);
        return newToken;
    } catch (error) {
        processQueue(error, null);
        clearAuthData();

        // Notify about refresh failure
        if (notificationCallback) {
            notificationCallback('Session expired. Please log in again.', 'error');
        }

        throw error;
    } finally {
        isRefreshing = false;
    }
};

// Request interceptor
ApiClient.interceptors.request.use(
    async (config) => {
        const token = localStorage.getItem('jwt');
        const refreshToken = localStorage.getItem('refreshToken');

        // If no tokens at all, let the request proceed (will likely get 401)
        if (!token && !refreshToken) {
            return config;
        }

        try {
            // If no access token but have refresh token, or token is expired
            if (!token || isTokenExpired(token)) {
                const newToken = await refreshAuthToken(true); // Silent refresh
                config.headers['Authorization'] = `Bearer ${newToken}`;
            } else {
                config.headers['Authorization'] = `Bearer ${token}`;
            }
        } catch (error) {
            // If refresh fails, clear auth and reject the request
            console.error('Token refresh failed in request interceptor:', error);
            clearAuthData();
            window.location.href = '/';
            return Promise.reject(error);
        }

        return config;
    },
    (error) => Promise.reject(error)
);

// Response interceptor
ApiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            try {
                const newToken = await refreshAuthToken(true); // Silent refresh
                originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                return ApiClient(originalRequest);
            } catch (refreshError) {
                clearAuthData();
                // Let the application handle the redirect
                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    }
);

// Helper methods for common operations
ApiClient.setAuthTokens = (token, refreshToken) => {

    localStorage.setItem('jwt', token);
    localStorage.setItem('refreshToken', refreshToken);
};

ApiClient.clearAuth = () => {
    clearAuthData();
};

ApiClient.getAuthToken = () => {
    return localStorage.getItem('jwt');
};

ApiClient.isAuthenticated = () => {
    const token = localStorage.getItem('jwt');
    return token && !isTokenExpired(token);
};

// Start periodic token refresh check (every minute)
const startTokenRefreshInterval = () => {
    stopTokenRefreshInterval(); // Clear any existing interval

    refreshInterval = setInterval(async () => {
        const token = localStorage.getItem('jwt');
        if (!token) {
            timeoutWarningShown = false;
            return;
        }

        try {
            const decoded = jwtDecode(token);
            const currentTime = Date.now() / 1000;
            const timeUntilExpiry = decoded.exp - currentTime;

            // Owner, 2026-09-03: never ask a human to click "Stay Logged In" when the system
            // holds a refresh token — the old 5-minute refresh vs 10-minute warning split let
            // the modal fire before the refresh branch could act. One policy decides
            // (sessionPolicy.js, pure and tested): REFRESH silently whenever possible; WARN
            // only when there is genuinely no refresh token to use.
            const decision = sessionRefreshDecision({
                timeUntilExpiry,
                hasRefreshToken: !!localStorage.getItem('refreshToken'),
                warningShown: timeoutWarningShown,
            });

            if (decision === 'REFRESH') {
                try {
                    await refreshAuthToken(true); // Silent — refreshAuthToken handles its own
                    timeoutWarningShown = false;  // failure (clears auth + notifies + throws).
                    console.log('Token refreshed automatically (silent)');
                } catch (refreshError) {
                    // Auth is already cleared by refreshAuthToken's own failure path; an
                    // expired token additionally returns the user to login, as before.
                    if (timeUntilExpiry <= 0) {
                        window.location.href = '/';
                    }
                }
            } else if (decision === 'WARN' && sessionTimeoutCallback) {
                sessionTimeoutCallback(Math.floor(Math.max(timeUntilExpiry, 0)));
                timeoutWarningShown = true;
            }
        } catch (error) {
            console.error('Token check failed:', error);
        }
    }, 60 * 1000); // Check every minute
};

const stopTokenRefreshInterval = () => {
    if (refreshInterval) {
        clearInterval(refreshInterval);
        refreshInterval = null;
    }
};

// Start the interval when authenticated
ApiClient.startTokenRefresh = () => {
    startTokenRefreshInterval();
};

// Stop the interval
ApiClient.stopTokenRefresh = () => {
    stopTokenRefreshInterval();
};

// Register notification callback
ApiClient.setNotificationCallback = (callback) => {
    notificationCallback = callback;
};

// Register session timeout callback
ApiClient.setSessionTimeoutCallback = (callback) => {
    sessionTimeoutCallback = callback;
};

// Method to manually extend session
ApiClient.extendSession = async () => {
    try {
        await refreshAuthToken();
        timeoutWarningShown = false; // Reset warning flag after manual refresh
        return true;
    } catch (error) {
        console.error('Failed to extend session:', error);
        return false;
    }
};

export default ApiClient;